import { PrimaryButtonMTV2, RejectButtonMT } from '@/components/buttons/Buttons';
import { EDIT_PEOPLE_FORM } from '@/constants/forms';
import { showNotification } from '@/modules/notification/engine/notification.library';
import IdGeneratorService from '@/services/id-generator.service';
import { Dialog } from 'primereact/dialog';
import { MdClose } from 'react-icons/md';
import SingleEditPeopleForm from '../components/SingleEditPeopleForm';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { createTypedModal, useTypedModal } from '@/common/common_utils';
import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { People } from '../engine/People';
import NiceModal from '@ebay/nice-modal-react';
import ConfirmModal from '../components/ConfirmModal';
import { Column, Row } from '@/components/container/Container';
import { TextHeadlineLarge, TextTitleMedium2 } from '@/components/text/Texts';

type EditType = 'single' | 'bulk';

interface Props {
	type: EditType;
	employeeId: number;
}

export const EditPeopleModal = createTypedModal<Props, undefined>(({ type, employeeId }: Props) => {
	const formName = new IdGeneratorService(EDIT_PEOPLE_FORM).formId || '';
	const modal = useTypedModal();


	const form = useForm<People.EmployeeForm>({
		resolver: yupResolver(People.employeeFormSchema),
		mode: 'all'
	});

	const employeeQ = useQuery({
		queryKey: ['SingleEditPeopleForm.employee'],
		queryFn: async () => await People.getEmployeeForm(employeeId),
	});

	useEffect(() => {
		if (employeeQ.data) form.reset(employeeQ.data);
	}, [employeeQ.data, form]);

	const handleCancelEditPeople = async () => {
		await NiceModal.show(ConfirmModal, {
			title: 'Discard changes?',
			message: 'You have unsaved changes. Are you sure you want to discard your changes?',
			acceptLabel: 'Discard Changes',
			rejectLabel: 'Cancel',
			onAccept: async () => modal.resolveRemove()
		});
	}

	// TODO implement change role confirmation
	const handleChangeRolePeople = async () => {
		await NiceModal.show(ConfirmModal, {
			title: 'Request Role Change',
			message: 'Are you sure you want to request a role change for this user? The request will be sent to the Super Admin for approval.',
			acceptLabel: 'Submit Request',
			rejectLabel: 'Cancel',
			onAccept: async () => {
				modal.resolveRemove();
				showNotification('Employee details updated. Awaiting Super Admin approval for the role change.');
			}
		});
	}

	return (
		<Dialog
			unstyled
			onHide={() => modal.resolveRemove()}
			visible={true}
			draggable={false}
			maskStyle={{ backgroundColor: '#00000047' }}
			closeIcon={(<MdClose size={24} className="text-onSurfaceVariant rounded-full duration-200 hover:bg-gray-200" />)}
			pt={{
				root: { className: 'py-6 px-8 rounded-[28px] w-[1136px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)] bg-white relative' },
				content: { className: 'p-0' },
				header: { className: 'absolute right-6 top-6' }
			}}
		>
			{/* Header */}
			{type === 'single' && (
				<Column className="gap-2 mb-10">
					<TextHeadlineLarge className="text-neutral17" text={`Edit ${employeeQ.data?.FirstName} ${employeeQ.data?.LastName}`} />
					<TextTitleMedium2 className="text-onSurfaceVariant" text="To proceed with this update, please provide the necessary details for the employee." />
				</Column>
			)}
			{type === 'bulk' && (<TextHeadlineLarge className="text-neutral17 mb-10" text="Bulk Edit" />)}

			{/* Form */}
			{type === 'single' && (<SingleEditPeopleForm formName={formName} form={form} />)}
			{type === 'bulk' && (<div>Bulk Edit</div>)}

			<Row className="mt-8 justify-end gap-4">
				<RejectButtonMT
					id="cancel-update-employee"
					label="Cancel"
					onClick={handleCancelEditPeople}
				/>
				<PrimaryButtonMTV2
					type="submit"
					id="update-employee"
					formName={formName}
					label="Update"
					disabled={!form.formState.isValid}
					onClick={form.handleSubmit(async (data: People.EmployeeForm) => {
						await People.sendEmployeeForm(data, false);
						modal.resolveRemove();
					})}
				/>
			</Row>
		</Dialog>
	);
});

export default EditPeopleModal;
